/**
 * Query-parameter scrubbing shared by every analytics and error-reporting sink.
 *
 * A private parameter can appear at the top level of a URL or inside another
 * parameter's value, for example a return path (`return_to=%2Flanding%3Foppref%3D…`)
 * or a pixel beacon that carries the page URL (`dl=https%3A%2F%2F…%3Foppref%3D…`).
 * Nested values are decoded, scrubbed recursively, and re-encoded. A string that
 * contains nothing to remove is returned unchanged, byte for byte.
 */

/** Opaque private references that must not reach analytics or error reporting. */
export const PRIVATE_REFERENCE_URL_PARAMS = ["oppref", "ct_attr"] as const;

const NESTED_URL_MAX_DEPTH = 3;
const RELATIVE_URL_BASE = "https://scrub.invalid";
// Absolute http(s) URLs and root-relative paths that carry a query, inside free
// text. No lookbehind: older Safari cannot parse it and the module would fail.
const URL_IN_TEXT_PATTERN = /(^|[\s"'(=,])(https?:\/\/[^\s"'<>]+|\/[^\s"'<>?]*\?[^\s"'<>]+)/gi;

function mentionsAnyKey(value: string, keys: readonly string[]): boolean {
  return keys.some((key) => value.includes(key));
}

function scrubUrlAtDepth(value: string, keys: readonly string[], depth: number): string {
  const isRelativePath = value.startsWith("/") && !value.startsWith("//");
  if (!isRelativePath && !/^https?:\/\//i.test(value)) return value;

  let parsed: URL;
  try {
    parsed = new URL(value, RELATIVE_URL_BASE);
  } catch {
    return value;
  }

  const rebuilt = new URLSearchParams();
  let changed = false;
  for (const [name, paramValue] of parsed.searchParams) {
    if (keys.includes(name)) {
      changed = true;
      continue;
    }

    let nextValue = paramValue;
    if (depth < NESTED_URL_MAX_DEPTH && mentionsAnyKey(paramValue, keys)) {
      const cleaned = scrubUrlAtDepth(paramValue, keys, depth + 1);
      if (cleaned !== paramValue) {
        nextValue = cleaned;
        changed = true;
      }
    }
    rebuilt.append(name, nextValue);
  }

  if (!changed) return value;

  parsed.search = rebuilt.toString();
  return isRelativePath ? `${parsed.pathname}${parsed.search}${parsed.hash}` : parsed.toString();
}

/**
 * Remove `keys` from an absolute http(s) URL or root-relative path, including
 * from URLs nested in its parameter values. Other strings are returned as is.
 */
export function scrubUrlParams(value: string, keys: readonly string[]): string {
  if (!value || !mentionsAnyKey(value, keys)) return value;
  return scrubUrlAtDepth(value, keys, 0);
}

/** Scrub every URL found inside free text, such as "GET https://…" span descriptions. */
export function scrubUrlsInText(text: string, keys: readonly string[]): string {
  if (!text || !mentionsAnyKey(text, keys)) return text;
  return text.replace(
    URL_IN_TEXT_PATTERN,
    (_match, boundary: string, url: string) => `${boundary}${scrubUrlParams(url, keys)}`
  );
}
